// Pure aggregations behind the Dashboard tab's insight modules (V5). Thin compositions over the
// existing issue/coverage spines (issues.ts, resolution-targets.ts, coverage.ts) so the dashboard
// can never disagree with the boards it links to.

import { ageDays } from './issues'
import { RESOLUTION_TARGETS, resolutionStatus, type ResolutionTargets } from './resolution-targets'

import type { GisIssue, MandalAggregate } from '../api/types'

/** How the OPEN load sits against the per-severity resolution targets (the aging read). */
export interface ResolutionMix {
  overdue: number
  dueSoon: number
  onTrack: number
  total: number
}

/** Bucket OPEN issues by their resolution status (callers pass the already-open subset). */
export function resolutionMix(
  openIssues: GisIssue[],
  targets: ResolutionTargets = RESOLUTION_TARGETS,
): ResolutionMix {
  const mix = { overdue: 0, dueSoon: 0, onTrack: 0, total: openIssues.length }
  for (const i of openIssues) {
    const status = resolutionStatus(i.severity, ageDays(i.createdAt), targets)
    if (status === 'overdue') {
      mix.overdue += 1
    } else if (status === 'due-soon') {
      mix.dueSoon += 1
    } else {
      mix.onTrack += 1
    }
  }
  return mix
}

/** One column of the open-issue aging histogram: a pending-duration band, its open count, and the
 *  critical subset within it (the urgent tail). */
export interface AgeBucket {
  key: string
  /** Full label (tooltip / legend), e.g. "1–2 weeks". */
  label: string
  /** Short axis tick, e.g. "1–2w". */
  short: string
  count: number
  critical: number
}

// Weekly bands up to a month, then an "over a month" catch-all tail (maxDays null). Ascending —
// the first band whose maxDays an age fits under wins. Exported so the age filter + its chips reuse it.
export const AGE_BANDS: { key: string; label: string; short: string; maxDays: number | null }[] = [
  { key: 'w1', label: '0–1 week', short: '0–1w', maxDays: 7 },
  { key: 'w2', label: '1–2 weeks', short: '1–2w', maxDays: 14 },
  { key: 'w3', label: '2–3 weeks', short: '2–3w', maxDays: 21 },
  { key: 'w4', label: '3–4 weeks', short: '3–4w', maxDays: 30 },
  { key: 'm1', label: 'Over a month', short: '>1mo', maxDays: null },
]

/** The band KEY a whole-day age falls into — the first band it fits under, else the over-a-month tail. */
export function ageBandKey(ageDaysValue: number): string {
  const idx = AGE_BANDS.findIndex((b) => b.maxDays !== null && ageDaysValue <= b.maxDays)
  return AGE_BANDS[idx === -1 ? AGE_BANDS.length - 1 : idx]!.key
}

/** Short label for a band key (chips / filter bar). */
export const ageBandShort = (key: string): string => AGE_BANDS.find((b) => b.key === key)?.short ?? key

/**
 * Bucket OPEN issues by how long they've been pending (age since `createdAt`) into weekly bands up
 * to a month, then an over-a-month tail. Callers pass the already-open subset. Always returns the
 * five bands in order (zeros included) so the chart's columns are stable.
 */
export function ageBuckets(openIssues: GisIssue[]): AgeBucket[] {
  const out: AgeBucket[] = AGE_BANDS.map((b) => ({
    key: b.key,
    label: b.label,
    short: b.short,
    count: 0,
    critical: 0,
  }))
  for (const i of openIssues) {
    const age = ageDays(i.createdAt)
    const found = AGE_BANDS.findIndex((b) => b.maxDays !== null && age <= b.maxDays)
    const band = out[found === -1 ? out.length - 1 : found]
    if (band === undefined) {
      continue
    }
    band.count += 1
    if (i.severity === 'critical') {
      band.critical += 1
    }
  }
  return out
}

/** One bar of a "top values" tally: the raw facet value (null = the fallback bucket), its display
 *  label, the count, and the critical subset (the bar's red leading segment). */
export interface TallyRow {
  value: string | null
  label: string
  count: number
  critical: number
}

export interface TallyResult {
  rows: TallyRow[]
  /** Distinct values folded away by the top-N cut (0 when everything fit). */
  more: number
}

/**
 * Count issues per `key` value, top-N by count (ties: label asc). Null keys fold into one
 * `fallbackLabel` bucket with `value: null` so callers can render it but skip the (impossible)
 * facet drill for it.
 */
export function tallyTop(
  list: GisIssue[],
  key: (i: GisIssue) => string | null,
  n: number,
  fallbackLabel: string,
): TallyResult {
  const buckets = new Map<string | null, { count: number; critical: number }>()
  for (const i of list) {
    const k = key(i)
    const b = buckets.get(k) ?? { count: 0, critical: 0 }
    b.count += 1
    if (i.severity === 'critical') {
      b.critical += 1
    }
    buckets.set(k, b)
  }
  const rows = [...buckets.entries()]
    .map(([value, b]) => ({ value, label: value ?? fallbackLabel, ...b }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
  return { rows: rows.slice(0, n), more: Math.max(0, rows.length - n) }
}

/** One mandal's survey-status mix for the coverage-by-mandal bars. */
export interface MandalCoverageRow {
  name: string
  surveyed: number
  inProgress: number
  overdue: number
  never: number
  total: number
  /** surveyed / total — matches the funnel/coverage headline methodology. */
  pct: number
}

export interface MandalCoverageResult {
  rows: MandalCoverageRow[]
  /** Mandals with sites that didn't make the worst-N cut. */
  more: number
}

/**
 * The worst-covered mandals, lowest surveyed-share first (ties: most sites first — the bigger gap
 * matters more). Mandals with no mapped sites are excluded (nothing to survey ≠ badly covered).
 */
export function worstCoverageMandals(mandals: MandalAggregate[], n: number): MandalCoverageResult {
  const rows = mandals
    .map((m) => {
      const total = m.surveyedSites + m.inProgressSites + m.overdueSites + m.neverSurveyedSites
      return {
        name: m.name,
        surveyed: m.surveyedSites,
        inProgress: m.inProgressSites,
        overdue: m.overdueSites,
        never: m.neverSurveyedSites,
        total,
        pct: total > 0 ? Math.round((m.surveyedSites / total) * 100) : 0,
      }
    })
    .filter((r) => r.total > 0)
    .sort((a, b) => a.pct - b.pct || b.total - a.total || a.name.localeCompare(b.name))
  return { rows: rows.slice(0, n), more: Math.max(0, rows.length - n) }
}
